import React, { useState } from 'react';
import {
  Chip,
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { FormattedMessage, useIntl } from 'react-intl';
import EditIcon from '@material-ui/icons/Edit';
import BlockIcon from '@material-ui/icons/Block';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import PropTypes from 'prop-types';
import { ConfirmationModal } from '../../components/molecules/ConfirmationModal.jsx';
import { palette } from '../../settings/themes/colors';
import { useStyles as useStylesTable } from '../../components/molecules/Table/useStyles';
import { HeadCell } from '../../components/molecules/Table/HeadCell.jsx';
import { BasicTableCell } from '../../components/molecules/Table/BasicTableCell.jsx';
import { orderStatuses } from './constants.js';
import { INSPECTION_TYPES } from '../Inspections/Inspections.jsx';

const PRIORITY_COLORS = {
  1: palette.candy,
  2: palette.LightRed,
  3: palette.yellow,
  4: palette.emerald,
  5: palette.captudataBlue
};

const STATUS_COLORS = {
  'workOrders.orderStatuses.pending': palette.dimGray,
  'workOrders.orderStatuses.done': palette.emerald,
  'workOrders.orderStatuses.cancelled': palette.LightRed,
  'workOrders.orderStatuses.inProgress': palette.captuDataYellow
};

const ORDER_STATUSES = Object.freeze({
  todo: 'todo',
  in_progress: 'in_progress',
  done: 'done',
  cancelled: 'cancelled'
});

const useStyles = makeStyles({
  root: {
    height: '73.9vh',
    '& tr ': {
      borderBottom: `1px solid ${palette.darkGrayTable}`
    }
  },
  dataCell: {
    backgroundColor: palette.white
  },
  chip: {
    backgroundColor: props => STATUS_COLORS[props.statusKey] || palette.captuDataYellow,
    color: palette.white
  },
  actionsCells: {
    padding: 0,
    '&>.container-buttons': {
      display: 'flex',
      borderRadius: 0,
      padding: 0,
      minHeight: 42,
      minWidth: 84,
      justifyContent: 'flex-end'
    }
  },
  clickDesabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: 0.5
  },
  Icon: {
    color: palette.cerulean
  }
});

const getOrderInspection = order => {
  const inspectionType = order.formularyId ? INSPECTION_TYPES.FORMULARY : INSPECTION_TYPES.ROUTE;

  const path = inspectionType === INSPECTION_TYPES.FORMULARY ? 'inspectionspunctual' : 'inspectionslineal';

  return `/operative/projects/${order.projectId}/${path}/${order.inspectionId}`;
};

const StatusChip = ({ statusKey, label }) => {
  const classes = useStyles({ statusKey });

  return <Chip label={label} className={classes.chip} />;
};

export const OrderList = ({
  datasource: { workOrders = [], meta = {} } = {},
  editionEnable,
  onDeleteOrder = () => {},
  onEditWorkOrder = () => {},
  onChangePagination = () => {}
}) => {
  const classes = useStyles();
  const classesTable = useStylesTable();
  const { messages: intlMessages } = useIntl();
  const [currentOrder, setCurrentOrder] = useState(null);
  const page = meta.currentPage ? meta.currentPage - 1 : 0;
  const rowsPerPage = meta.perPage || 25;

  const TABLE_COLUMNS_HEADER = [
    { label: intlMessages['workOrders.header.number'] },
    { label: intlMessages['workOrders.header.estimatedDate'] },
    { label: intlMessages['workOrders.header.inspector'] },
    { label: intlMessages['common.project'] },
    { label: intlMessages['workOrders.header.type'] },
    { label: intlMessages['common.status'] },
    { label: intlMessages['workOrders.header.priority'] },
    { label: intlMessages['common.actions'], align: 'right' }
  ];

  const handleChangePage = (_, newPage) => {
    onChangePagination({
      page: newPage + 1,
      perPage: rowsPerPage
    });
  };

  const handleChangeRowsPerPage = event => {
    const newPerPage = parseInt(event.target.value, 10);

    onChangePagination({
      page: 1,
      perPage: newPerPage
    });
  };

  const handleOnClickDelete = order => setCurrentOrder(order);

  const handleOnConfirmDelete = () => {
    currentOrder && onDeleteOrder(currentOrder.id);
    setCurrentOrder(null);
  };

  const handleOnCancelDelete = () => setCurrentOrder(null);

  return (
    <>
      <div className={classes.root}>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_COLUMNS_HEADER.map((column, index) => (
                <HeadCell key={column.id + index} align={column.align}>
                  <Typography>{column.label}</Typography>
                </HeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workOrders.map(order => (
              <TableRow key={order.id} className={classes.dataCell}>
                <BasicTableCell>
                  <Typography>{order.id}</Typography>
                </BasicTableCell>
                <BasicTableCell>
                  <Typography>{order.expectedCompletionDate}</Typography>
                </BasicTableCell>
                <BasicTableCell>
                  <Typography>{(order.assignee || {}).fullName}</Typography>
                </BasicTableCell>
                <BasicTableCell>
                  <Typography>{(order.project || {}).name}</Typography>
                </BasicTableCell>
                <BasicTableCell>
                  <Typography>{intlMessages[`workOrders.inspectionType.${order.formulary ? 'byForm' : 'byTracking'}`]}</Typography>
                </BasicTableCell>
                <BasicTableCell>
                  <StatusChip
                    statusKey={orderStatuses.find(os => os.id === order.status).i18nId}
                    label={orderStatuses.find(os => os.id === order.status).label}
                  />
                </BasicTableCell>
                <BasicTableCell>
                  <Grid container justifyContent="center" style={{ width: '100%' }}>
                    <Grid item>
                      <Tooltip arrow title={<FormattedMessage id={orderStatuses.find(os => os.id === order.status).i18nId} />}>
                        {order.priority < 4 ? (
                          <ArrowUpwardIcon fontSize="small" style={{ color: PRIORITY_COLORS[order.priority] }} />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" style={{ color: PRIORITY_COLORS[order.priority] }} />
                        )}
                      </Tooltip>
                    </Grid>
                  </Grid>
                </BasicTableCell>
                <BasicTableCell className={classes.actionsCells} align="right">
                  <div className="container-buttons">
                    {editionEnable && order.status === 'todo' && (
                      <Tooltip title={<FormattedMessage id="common.edit" />} arrow>
                        <IconButton
                          aria-label={intlMessages['workOrders.tooltip.viewInspection']}
                          size="small"
                          onClick={() => onEditWorkOrder(order)}
                          className={classesTable.actionIcon}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!!order.inspectionId && (
                      <Tooltip title={<FormattedMessage id="workOrders.tooltip.viewInspection" />} arrow>
                        <IconButton
                          aria-label={intlMessages['workOrders.tooltip.viewInspection']}
                          size="small"
                          href={getOrderInspection(order)}
                          className={classesTable.actionIcon}
                        >
                          <VisibilityOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editionEnable && order.status === 'todo' && (
                      <Tooltip title={<FormattedMessage id="common.cancel" />} arrow>
                        <IconButton
                          aria-label={intlMessages['common.cancel']}
                          onClick={() => handleOnClickDelete(order)}
                          className={classesTable.actionIcon}
                        >
                          <BlockIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editionEnable && order.status === 'cancelled' && (
                      <Tooltip title={<FormattedMessage id="workOrders.tooltip.delete" />} arrow>
                        <IconButton aria-label={intlMessages['workOrders.tooltip.delete']} onClick={() => handleOnClickDelete(order)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </BasicTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={classes.dataCell}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={meta.totalCount}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage={intlMessages['table.rowsPerPage.inspector']}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>
      <ConfirmationModal
        open={currentOrder !== null}
        onClose={handleOnCancelDelete}
        onConfirm={handleOnConfirmDelete}
        onCancel={handleOnCancelDelete}
        title={
          <FormattedMessage
            id={(currentOrder || {}).status === ORDER_STATUSES.cancelled ? 'workOrders.deleteOrder' : 'workOrders.cancelOrder'}
            values={{ orderId: (currentOrder || {}).id }}
          />
        }
        confirmText={<FormattedMessage id="system.actions.yes" />}
      />
    </>
  );
};

OrderList.propTypes = {
  datasource: PropTypes.shape({
    workOrders: PropTypes.array,
    meta: PropTypes.object
  }).isRequired,
  editionEnable: PropTypes.bool,
  onDeleteOrder: PropTypes.func,
  onEditWorkOrder: PropTypes.func,
  onChangePagination: PropTypes.func
};

StatusChip.propTypes = {
  statusKey: PropTypes.string,
  label: PropTypes.string
};
